import { LayersList } from '@deck.gl/core';
import { GeoJsonLayer } from '@deck.gl/layers';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useJsApiLoader } from '@react-google-maps/api';
import { useQuery } from '@tanstack/react-query';
import {
  getV1CompaniesCompanyIdJobsJobIdSiteUsages,
  getV1CompaniesCompanyIdOrdersOrderIdSiteUsages,
  JobEventName,
  WaypointType,
} from '@treadinc/horizon-api-spec';
import { point } from '@turf/helpers';
import { t } from 'i18next';
import { flatten, isNil } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import {
  ComponentProps,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  LoadingSpinner,
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { DEFAULT_MAP_CENTER } from '~constants/mapConsts';
import { Company } from '~hooks/useCompany/models';
import { useCompany } from '~hooks/useCompany/useCompany';
import { Job, type JobTripEvent } from '~hooks/useJob/models';
import { useJob } from '~hooks/useJob/useJob';
import { LiveSiteData } from '~hooks/useLiveMap/models';
import { useBatchedTruckUpdates } from '~hooks/useLiveMap/useBatchedTruckUpdates';
import { GeofencePickInfo, useGeofenceLayer } from '~hooks/useLiveMap/useGeofenceLayer';
import { useLiveMap } from '~hooks/useLiveMap/useLiveMap';
import { useLiveSiteLocations } from '~hooks/useLiveMap/useLiveSiteLocations';
import { SitePickInfo, useSiteLayer } from '~hooks/useLiveMap/useSiteLayer';
import { TruckPickInfo, useTruckLayer } from '~hooks/useLiveMap/useTruckLayer';
import { useTruckLocations } from '~hooks/useLiveMap/useTruckLocations';
import { Load, LoadCycle } from '~hooks/useLoad/models';
import type { LocationHistoryMinimal } from '~hooks/useLocationHistory/models';
import { NextBillionAssetLocation } from '~hooks/useNextBillionAssetLocationHistories/models';
import { useNextBillionAssetLocationHistories } from '~hooks/useNextBillionAssetLocationHistories/useNextBillionAssetLocationHistories';
import { Order } from '~hooks/useOrders/models';
import { Geofence, Site, SiteBasic, WayPoint } from '~hooks/useSites/models';
import { useSites } from '~hooks/useSites/useSites';
import { JobSiteUsage, OrderSiteUsage } from '~hooks/useSiteUsages/models';
import BackToLiveBar from '~pages/LiveMap/BackToLiveBar';
import { TimeIndicatorContextProvider } from '~pages/LiveMap/Timeline/components/hooks/useTimeIndicatorContext';
import { Timeline } from '~pages/LiveMap/Timeline/Timeline';
import { ROUTES } from '~router/routePaths';
import { useStores } from '~store';
import fetchAllPages from '~utils/fetchAllPages';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';
import { hexToRgbaArray } from '~utils/utilFunctions';

import MapConfigurationControl, {
  MapConfigurationOptionKey,
} from '../Controls/MapConfigurationControl';
import {
  GeofenceType,
  GOOGLE_MAPS_LOADER_OPTIONS,
  HIGHLIGHT_MARKER_RADIUS,
  MarkerType,
  ROUTE_MARKER_RADIUS,
} from './constants';
import { MapV2 } from './MapV2';
import { buildRoutePingLayer } from './routePingLayer';
import { SitePopoverManager } from './SitePopoverManager';
import { TruckPopoverManager } from './TruckPopoverManager';
import { TreadMarkerProps } from './types';
import { buildCombinedSiteMarkers } from './utils/siteMarkers';
import {
  CircleGeofenceDatum,
  extractCircleGeofences,
  extractPolygonGeofences,
  PolygonGeofenceDatum,
  SiteLayerDatum,
  transformSiteLocations,
} from './utils/transformSiteData';
import {
  separateTruckAndMovingSites,
  transformTruckLocations,
  TruckLayerDatum,
} from './utils/transformTruckData';

const emDash = '—';

const isPauseResumeEvent = (e: { eventName: string }) =>
  e.eventName === JobEventName.JOB_PAUSED || e.eventName === JobEventName.JOB_RESUMED;

interface OverlayFeature {
  properties: {
    color: [number, number, number, number]; // RGBA color format
    radius: number; // Point radius in pixels
    lineWidth: number; // Line width in pixels
    borderColor?: [number, number, number, number]; // Optional RGBA border color
  };
}

/*
 * Consider this the "opinionated" part of our mapping solution. This component transforms jobs,
 * orders, and local truck data into a more presentation-heavy BaseMap.
 */
const TreadLiveMapV2 = ({
  isTimelineVisible,
  job,
  lazyLoadJobPings = false,
  loads,
  loadCycles,
  showHoveredLoadStats = false,
  onBackClick,
  onTimelineVisibilityChange,
  onViewJobRoute,
  order,
  geofenceRecenterPosition,
  geofenceDrawingMode,
  geofenceDrawingCoordinates,
  onGeofencePolygonEdit,
  geofenceCircleRadius,
  onGeofenceCircleEdit,
  isMapPickMode,
  onMapLocationPick,
  geofenceRoutePings,
  geofenceRouteJob,
  isLoadingGeofenceRoutePings,
  highlightedPoint,
  centerOnPoint,
  hideMapControls = false,
  geofencePickupCenter = null,
  geofenceDropoffCenter = null,
  readOnlyGeofencePolygon = null,
  readOnlyGeofenceCircle = null,
  onGeofenceLabelClick,
  onGeofenceLabelDrag,
  onGeofenceLabelDragEnd,
  onGeofenceLocationDragEnd,
  extraActions,
  hideRouteHistoryAction = false,
}: {
  extraActions?: ReactNode;
  hideRouteHistoryAction?: boolean;
  isTimelineVisible?: boolean;
  job?: Job;
  lazyLoadJobPings?: boolean;
  loads?: Load[];
  loadCycles?: LoadCycle[];
  showHoveredLoadStats?: boolean;
  onBackClick?: () => void;
  onTimelineVisibilityChange?: (visible: boolean) => void;
  onViewJobRoute?: (job: Job) => void;
  order?: Order;
  geofenceRecenterPosition?: { lat: number; lng: number } | null;
  geofenceDrawingMode?: boolean;
  geofenceDrawingCoordinates?: { lat: number; lng: number }[];
  onGeofencePolygonEdit?: (coordinates: { lat: number; lng: number }[]) => void;
  geofenceCircleRadius?: number | null;
  /** Called when the user drags the circle center or circumference (editable circle). */
  onGeofenceCircleEdit?: (center: { lat: number; lng: number }, radius: number) => void;
  isMapPickMode?: boolean;
  onMapLocationPick?: (coordinates: { lat: number; lng: number }) => void;
  geofenceRoutePings?: LocationHistoryMinimal[] | null;
  geofenceRouteJob?: Job;
  isLoadingGeofenceRoutePings?: boolean;
  /** Optional point to highlight on the map (e.g. from table row hover in Job Geo Debug). */
  highlightedPoint?: { lat: string; lon: string } | null;
  /** When set, the map pans to this point (e.g. from table row click in Job Geo Debug). */
  centerOnPoint?: { lat: string; lon: string } | null;
  /** When true, hides the map configuration and layers toggle buttons (e.g. Job Geo Debug). */
  hideMapControls?: boolean;
  /** Orange pickup icon position for naive/accuracy_strict geofences (Job Geo Debug). */
  geofencePickupCenter?: { lat: number; lng: number } | null;
  /** Orange dropoff icon position for naive/accuracy_strict geofences (Job Geo Debug). */
  geofenceDropoffCenter?: { lat: number; lng: number } | null;
  /** Read-only geofence polygon (other site when editing one on Job Geo Debug). */
  readOnlyGeofencePolygon?: { lat: number; lng: number }[] | null;
  /** Read-only geofence circle (other site when editing one on Job Geo Debug). */
  readOnlyGeofenceCircle?: {
    center: { lat: number; lng: number };
    radius: number;
  } | null;
  /** When user clicks the orange pickup/dropoff icon (Job Geo Debug), switch tab. */
  onGeofenceLabelClick?: (type: 'pickup' | 'dropoff') => void;
  /** Fired during icon drag so the geofence can move with the icon (Job Geo Debug). */
  onGeofenceLabelDrag?: (
    type: 'pickup' | 'dropoff',
    position: { lat: number; lng: number },
  ) => void;
  /** When user finishes dragging the orange pickup/dropoff icon (Job Geo Debug), move the geofence and recalc. */
  onGeofenceLabelDragEnd?: (
    type: 'pickup' | 'dropoff',
    position: { lat: number; lng: number },
  ) => void;
  /** When user drags the geofence location pin (e.g. Geofence Reports), update location and optionally polygon. */
  onGeofenceLocationDragEnd?: (position: { lat: number; lng: number }) => void;
}) => {
  const theme = useTheme();
  // MapConfigurationControl's `position` reads `window.google.maps.ControlPosition`
  // while this component builds its JSX — above MapV2's own `isLoaded` gate — so the
  // Live Map needs a gate of its own or the entire render throws before the SDK
  // resolves (Sentry HORIZON-FE-2Y9). Passing the same shared options object joins
  // MapV2's in-flight load instead of starting a second one.
  const { isLoaded: isGoogleMapsLoaded } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);
  const [truckLocationsById, setTruckLocationsById] = useState<
    Record<string, NextBillionAssetLocation>
  >({});
  const [liveSiteMarkersById, setLiveSiteMarkersById] = useState<
    Record<string, LiveSiteData>
  >({});
  const [siteDetailsBySiteId, setSiteDetailsBySiteId] = useState<Record<
    string,
    Site
  > | null>(null);
  // Additional geofences for sites (beyond the primary geofence)
  const [additionalGeofencesBySiteId, setAdditionalGeofencesBySiteId] = useState<
    Record<string, Geofence[]>
  >({});

  const [jobRoutePings, setJobRoutePings] = useState<LocationHistoryMinimal[] | null>(
    null,
  );
  const [isLoadingRoutePings, setIsLoadingRoutePings] = useState(false);
  const [jobPauseResumeEvents, setJobPauseResumeEvents] = useState<JobTripEvent[]>([]);
  const [selectedJobRoutePingInd, setSelectedJobRoutePingInd] = useState<number | null>(
    null,
  );

  const [jobMovingSiteRoutePings, setJobMovingSiteRoutePings] = useState<
    LocationHistoryMinimal[] | null
  >(null);
  const [isLoadingMovingSiteRoutePings, setIsLoadingMovingSiteRoutePings] =
    useState(false);
  const [selectedJobMovingSiteRoutePingInd, setSelectedJobMovingSiteRoutePingInd] =
    useState<number | null>(null);

  // State for geofence route ping selection (used by Timeline scrubber in Geofence Reports)
  const [selectedGeofenceRoutePingInd, setSelectedGeofenceRoutePingInd] = useState<
    number | null
  >(null);

  // Reset geofence route ping selection when viewing a different route
  useEffect(() => {
    setSelectedGeofenceRoutePingInd(null);
  }, [geofenceRoutePings]);

  const { userStore } = useStores();
  const { getCompanyById } = useCompany();
  const [currentCompany, setCurrentCompany] = useState<Company | null>(null);

  // Track previous order/job/company IDs to detect view changes
  const prevOrderIdRef = useRef<string | undefined>(undefined);
  const prevJobIdRef = useRef<string | undefined>(undefined);
  const prevCompanyIdRef = useRef<string | undefined>(undefined);
  // Flag to trigger bounds reset when view changes (select/deselect order/job or company change)
  const [shouldResetBounds, setShouldResetBounds] = useState(false);

  const currentCompanyId = getEffectiveUserCompanyId(userStore);

  // Detect view changes and trigger bounds reset
  useEffect(() => {
    const orderChanged = prevOrderIdRef.current !== order?.id;
    const jobChanged = prevJobIdRef.current !== job?.id;
    const companyChanged = prevCompanyIdRef.current !== currentCompanyId;

    if (orderChanged || jobChanged || companyChanged) {
      setShouldResetBounds(true);
    }

    // Update refs
    prevOrderIdRef.current = order?.id;
    prevJobIdRef.current = job?.id;
    prevCompanyIdRef.current = currentCompanyId;
  }, [order?.id, job?.id, currentCompanyId]);

  // Stable callback to clear bounds reset flag
  const handleBoundsReset = useCallback(() => {
    setShouldResetBounds(false);
  }, []);

  const { getLatestLocations } = useNextBillionAssetLocationHistories();
  const { getCompanySitesDispatchIndex } = useSites();
  const { fetchTreadRoute, fetchTreadMovingGeofenceRoute } = useLiveMap(currentCompanyId);
  const { getAllJobEvents } = useJob();
  const { subscribeToLastLocationUpdates } = useTruckLocations();
  const { subscribeToLiveSiteUpdates } = useLiveSiteLocations();
  const [showVendorName, setShowVendorName] = useState(false);
  const [colorByDestination, setColorByDestination] = useState(false);

  // State for DeckGL truck layer interactions
  const [hoveredTruck, setHoveredTruck] = useState<TruckLayerDatum | null>(null);
  const [clickedTruck, setClickedTruck] = useState<TruckLayerDatum | null>(null);
  const [popoverAnchorPosition, setPopoverAnchorPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [activeMarkerId, setActiveMarkerId] = useState<string | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);

  // State for DeckGL site layer interactions
  const [clickedSite, setClickedSite] = useState<SiteLayerDatum | null>(null);
  const [sitePopoverAnchorPosition, setSitePopoverAnchorPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [activeSiteId, setActiveSiteId] = useState<string | null>(null);

  // State for DeckGL geofence layer interactions
  const [clickedGeofence, setClickedGeofence] = useState<
    CircleGeofenceDatum | PolygonGeofenceDatum | null
  >(null);
  const [isClickedGeofenceCircle, setIsClickedGeofenceCircle] = useState(false);
  const [hoveredGeofenceId, setHoveredGeofenceId] = useState<string | null>(null);

  /**
   * Batched truck updates hook - reduces re-renders by batching WebSocket updates.
   * Updates are collected over a 100ms window and flushed together.
   */
  const { addUpdate: addBatchedTruckUpdate, clearPending: clearPendingTruckUpdates } =
    useBatchedTruckUpdates({
      onFlush: useCallback((updates: Map<string, NextBillionAssetLocation>) => {
        setTruckLocationsById((prev) => {
          const newState = { ...prev };
          updates.forEach((location, id) => {
            newState[id] = location;
          });
          return newState;
        });
      }, []),
      /**
       * Batching window for truck location updates (ms).
       *
       * 100ms was chosen based on performance testing and these factors:
       * - WebSocket messages typically arrive at 50-100ms intervals under normal load
       * - Results in max 10 state updates/second, which prevents excessive React re-renders
       * - Provides smooth, near-real-time updates without sacrificing performance
       * - Balances perceived responsiveness vs computational cost with thousands of markers
       *
       * Lower values (< 50ms):
       * - Increase re-render frequency, causing performance degradation with many trucks
       * - Minimal improvement to perceived real-time accuracy
       *
       * Higher values (> 200ms):
       * - May cause noticeable "jumpy" truck movements
       * - Reduces responsiveness of the live map experience
       */
      batchWindowMs: 100,
    });

  const movingSiteData = useMemo(() => {
    const movingSite = Object.values(siteDetailsBySiteId ?? {}).find(
      (site) => site.movingSite,
    );
    if (!movingSite)
      return {
        id: null,
        radius: null,
      };
    return {
      id: movingSite?.movingSite?.id ?? null,
      radius: movingSite?.nextBillionGeofence?.circleRadius ?? null,
    };
  }, [siteDetailsBySiteId]);

  const getLatestTruckLocations = useCallback(async () => {
    const truckLocations = [];
    const locationResponse = await getLatestLocations({
      job_id: job?.id,
      order_id: order?.id,
      companyId: currentCompanyId,
    });

    if (locationResponse) {
      const { data } = locationResponse;
      truckLocations.push(data);
    }

    return flatten(truckLocations).flatMap((item) => item);
  }, [currentCompanyId]);

  const getLiveSiteLocations = useCallback(
    (waypointType: 'pickup' | 'drop_off') =>
      fetchAllPages((cursor, limit) =>
        getCompanySitesDispatchIndex({
          companyId: currentCompanyId,
          cursor,
          limit,
          siteType: waypointType,
        }),
      ),
    [currentCompanyId],
  );

  const SitePopover = ({ site }: { site: Site }) => {
    return (
      <Box
        sx={{
          p: 1,
          borderRadius: 1,
          display: 'grid',
          gridTemplateColumns: 'auto auto',
          gap: 0.5,
          maxWidth: '240px',
        }}
      >
        <Typography noWrap variant="h6" sx={{ gridColumn: 'span 2', fontSize: '14px' }}>
          {site.name}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          {t('live_map.pop_up.site.address')}
        </Typography>
        <Typography variant="body2">{site.address?.thoroughfare ?? emDash}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          {t('live_map.pop_up.site.type')}
        </Typography>
        <Typography variant="body2">{site.siteType || emDash}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          {t('live_map.pop_up.site.geofence_type')}
        </Typography>
        <Typography variant="body2">
          {site.nextBillionGeofence?.geofenceType
            ? site.nextBillionGeofence.geofenceType.replace(
                /^./,
                site.nextBillionGeofence.geofenceType[0].toUpperCase(),
              )
            : emDash}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          {t('live_map.pop_up.site.external_id')}
        </Typography>
        <Typography variant="body2">
          {site.externalId?.length ? site.externalId : emDash}
        </Typography>
      </Box>
    );
  };

  useEffect(() => {
    const fetchCompany = async () => {
      if (!currentCompanyId) {
        return;
      }

      const company = await getCompanyById({ id: currentCompanyId });
      setCurrentCompany(company ?? null);
    };

    fetchCompany();
  }, [currentCompanyId]);

  // Truck locations are primary data we always need. Load and then listen for changes.
  useEffect(() => {
    let truckLocationSubscription: ReturnType<
      typeof subscribeToLastLocationUpdates
    > | null = null;

    const loadAndSubscribeToTruckLocations = async () => {
      // Initial load - set all locations at once (no need to batch initial data)
      const data = await getLatestTruckLocations();
      setTruckLocationsById(
        data.reduce(
          (acc, location) => ({
            ...acc,
            [location.equipment?.id ?? location.id]: location,
          }),
          {},
        ),
      );

      // Subscribe to real-time updates with batching for performance
      if (currentCompanyId) {
        truckLocationSubscription = subscribeToLastLocationUpdates({
          companyId: currentCompanyId,
          onMessageCallBack: (data: NextBillionAssetLocation) => {
            // Use batched updates to reduce re-render frequency
            // Updates are collected over 100ms and flushed together
            addBatchedTruckUpdate(data);
          },
        });
      }
    };
    loadAndSubscribeToTruckLocations();

    return () => {
      truckLocationSubscription?.unsubscribe?.();
      clearPendingTruckUpdates(); // Clear any pending batched updates
      setTruckLocationsById({});
    };
  }, [currentCompanyId, addBatchedTruckUpdate, clearPendingTruckUpdates]);

  /**
   * Determine if this is an order/job-specific view (Dispatch expanded view).
   * In this context, we should only show sites from the specific order/job,
   * not all live company sites.
   */
  const isOrderOrJobSpecificView = !!(order || job);

  useEffect(() => {
    // Skip loading all live sites when viewing a specific order/job
    // In that context, we only want to show the order/job's own sites
    if (isOrderOrJobSpecificView) {
      return;
    }

    let liveSiteSubscription: ReturnType<typeof subscribeToLiveSiteUpdates> | null = null;

    const loadAndSubscribeToLiveSites = async () => {
      const pickupSiteData = await getLiveSiteLocations(WaypointType.PICKUP);
      const dropoffSiteData = await getLiveSiteLocations(WaypointType.DROP_OFF);
      const newLiveSiteMarkersById: Record<string, LiveSiteData> = {};
      pickupSiteData.forEach((site) => {
        const markerWaypointType =
          newLiveSiteMarkersById[site.id] &&
          newLiveSiteMarkersById[site.id].waypointType !== WaypointType.PICKUP
            ? 'both'
            : WaypointType.PICKUP;

        if (!site.lat || !site.lon) return;
        newLiveSiteMarkersById[site.id] = new LiveSiteData(
          new SiteBasic(
            site.id,
            site.name,
            site.lat.toString(),
            site.lon.toString(),
            site.latLon,
            site.nextBillionGeofence,
            site.fullAddress,
            site.discardedAt,
          ),
          markerWaypointType,
        );
      });

      dropoffSiteData.forEach((site) => {
        const markerWaypointType =
          newLiveSiteMarkersById[site.id] &&
          newLiveSiteMarkersById[site.id].waypointType !== WaypointType.DROP_OFF
            ? 'both'
            : WaypointType.DROP_OFF;

        if (!site.lat || !site.lon) return;
        newLiveSiteMarkersById[site.id] = new LiveSiteData(
          new SiteBasic(
            site.id,
            site.name,
            site.lat.toString(),
            site.lon.toString(),
            site.latLon,
            site.nextBillionGeofence,
            site.fullAddress,
            site.discardedAt,
          ),
          markerWaypointType,
        );
      });

      setLiveSiteMarkersById(newLiveSiteMarkersById);

      if (currentCompanyId) {
        liveSiteSubscription = subscribeToLiveSiteUpdates({
          companyId: currentCompanyId,
          onMessageCallBack: (data: LiveSiteData) => {
            setLiveSiteMarkersById((prev) => {
              if (
                prev[data.site.id] &&
                data.waypointType !== prev[data.site.id].waypointType
              ) {
                // if the site is already in the map, set the marker type to both_site
                data.waypointType = 'both';
              }
              return {
                ...prev,
                [data.site.id]: data,
              };
            });
          },
        });
      }
    };
    loadAndSubscribeToLiveSites();
    return () => {
      liveSiteSubscription?.unsubscribe?.();
      setLiveSiteMarkersById({});
    };
  }, [currentCompanyId, isOrderOrJobSpecificView]);

  // Fetch site_usages for the current order or job. React Query handles
  // cancellation, dedup, and stale-data hygiene: when the query key changes,
  // the in-flight fetch is aborted (via the signal passed to the SDK call)
  // and `query.data` reverts to undefined until the new fetch resolves, so
  // the combinedSiteMarkersById memo falls back to the fresh
  // job/order.waypoints in the meantime.
  const orderSiteUsagesQuery = useQuery({
    queryKey: ['order-site-usages', currentCompanyId, order?.id],
    enabled: Boolean(order?.id && currentCompanyId),
    queryFn: async ({ signal }) => {
      const res = await getV1CompaniesCompanyIdOrdersOrderIdSiteUsages({
        path: { 'company-id': currentCompanyId!, 'order-id': order!.id },
        signal,
      });
      return (res.data.data ?? []).map(OrderSiteUsage.parse);
    },
  });

  const jobSiteUsagesQuery = useQuery({
    queryKey: ['job-site-usages', currentCompanyId, job?.id],
    enabled: Boolean(job?.id && currentCompanyId),
    queryFn: async ({ signal }) => {
      const res = await getV1CompaniesCompanyIdJobsJobIdSiteUsages({
        path: { 'company-id': currentCompanyId!, 'job-id': job!.id },
        signal,
      });
      return (res.data.data ?? []).map(JobSiteUsage.parse);
    },
  });

  const orderSiteUsages = useMemo(
    () => orderSiteUsagesQuery.data ?? [],
    [orderSiteUsagesQuery.data],
  );
  const jobSiteUsages = useMemo(
    () => jobSiteUsagesQuery.data ?? [],
    [jobSiteUsagesQuery.data],
  );

  // Apply site_details and additional geofences whenever new site_usages land.
  // Each query owns its own apply effect so cached data from a disabled query
  // can't leak into the active view's site details.
  const applySiteUsages = useCallback((siteUsages: (JobSiteUsage | OrderSiteUsage)[]) => {
    const newSiteDetails: Record<string, Site> = {};
    const newAdditionalGeofences: Record<string, Geofence[]> = {};

    siteUsages.forEach((su) => {
      newSiteDetails[su.site.id] = su.site;
      if (su.additionalGeofences.length > 0) {
        newAdditionalGeofences[su.site.id] = su.additionalGeofences;
      }
    });

    setSiteDetailsBySiteId((prev) => ({ ...(prev ?? {}), ...newSiteDetails }));
    setAdditionalGeofencesBySiteId((prev) => ({
      ...prev,
      ...newAdditionalGeofences,
    }));
  }, []);

  useEffect(() => {
    if (orderSiteUsagesQuery.data?.length) applySiteUsages(orderSiteUsagesQuery.data);
  }, [orderSiteUsagesQuery.data, applySiteUsages]);

  useEffect(() => {
    if (jobSiteUsagesQuery.data?.length) applySiteUsages(jobSiteUsagesQuery.data);
  }, [jobSiteUsagesQuery.data, applySiteUsages]);

  useEffect(() => {
    if (!job?.id) return;
    getAllJobEvents(job.id, currentCompanyId)
      .then((events) => {
        setJobPauseResumeEvents(events.filter(isPauseResumeEvent));
      })
      .catch(() => {
        // Error already surfaced via handleRequestError (toast + Sentry)
      });
  }, [job?.id, currentCompanyId, getAllJobEvents]);

  // Pings are secondary data we only ask for when a job is present.
  // We also only eagerly load if the lazyLoadJobPings flag is false or empty.
  useEffect(() => {
    if (!job?.id || lazyLoadJobPings) return;
    setIsLoadingRoutePings(true);
    fetchTreadRoute(job.id).then((data) => {
      setJobRoutePings(data);
      setIsLoadingRoutePings(false);
    });

    if (movingSiteData.id) {
      setIsLoadingMovingSiteRoutePings(true);
      fetchTreadMovingGeofenceRoute(job.id, movingSiteData.id)
        .then((data) => setJobMovingSiteRoutePings(data))
        .finally(() => setIsLoadingMovingSiteRoutePings(false));
    }
  }, [
    job?.id,
    lazyLoadJobPings,
    movingSiteData,
    setJobRoutePings,
    setIsLoadingRoutePings,
    setJobMovingSiteRoutePings,
    setIsLoadingMovingSiteRoutePings,
  ]);

  const mapCenter = useMemo(() => {
    const lat = currentCompany?.defaultLat;
    const lng = currentCompany?.defaultLon;

    return {
      lng: (Number.isFinite(lng) ? lng : DEFAULT_MAP_CENTER.lng) as number,
      lat: (Number.isFinite(lat) ? lat : DEFAULT_MAP_CENTER.lat) as number,
    };
  }, [currentCompany]);

  const handleViewRoute = (job: Job) => {
    if (onViewJobRoute) {
      onTimelineVisibilityChange?.(true);

      setIsLoadingRoutePings(true);
      fetchTreadRoute(job.id)
        .then((data) => setJobRoutePings(data))
        .finally(() => setIsLoadingRoutePings(false));

      if (movingSiteData.id) {
        setIsLoadingMovingSiteRoutePings(true);
        fetchTreadMovingGeofenceRoute(job.id, movingSiteData.id)
          .then((data) => setJobMovingSiteRoutePings(data))
          .finally(() => setIsLoadingMovingSiteRoutePings(false));
      }
      onViewJobRoute(job);
    }
  };

  /**
   * Transform truck locations into DeckGL-compatible data format.
   * This replaces the previous approach that created React components for each truck.
   *
   * Key performance optimization:
   * - Creates plain data objects instead of React components
   * - Popover content is rendered on-demand via TruckPopoverManager
   * - Enables GPU-accelerated rendering via DeckGL IconLayer
   */
  const truckLayerData = useMemo(
    () =>
      transformTruckLocations(truckLocationsById, {
        orderId: order?.id,
        jobId: job?.id,
        showVendorName,
        colorByDestination,
        activeMarkerId,
      }),
    [
      truckLocationsById,
      showVendorName,
      colorByDestination,
      job?.id,
      order?.id,
      activeMarkerId,
    ],
  );

  /**
   * Separate trucks from moving sites for different rendering strategies.
   * Moving sites need circles rendered around them (via Google Maps API).
   */
  const { trucks: regularTrucks, movingSites } = useMemo(
    () => separateTruckAndMovingSites(truckLayerData),
    [truckLayerData],
  );

  /**
   * Handle DeckGL truck hover events.
   * Updates state for popover rendering and active marker highlighting.
   * Converts DeckGL canvas coordinates to viewport coordinates for popover positioning.
   */
  const handleTruckHover = useCallback(
    (info: TruckPickInfo | null) => {
      if (info) {
        setHoveredTruck(info.truck);
        // Convert canvas-relative coordinates to viewport coordinates
        const rect = mapContainerRef.current?.getBoundingClientRect();
        const offsetX = rect?.left ?? 0;
        const offsetY = rect?.top ?? 0;
        // Only update anchor position from hover when there is no clicked truck.
        // If a clicked truck exists, we want to keep the popover pinned to that position.
        if (!clickedTruck) {
          setPopoverAnchorPosition({ x: info.x + offsetX, y: info.y + offsetY });
        }
        setActiveMarkerId(info.truck.id);
      } else {
        setHoveredTruck(null);
        // When hover ends, restore activeMarkerId to clicked truck if one exists
        // Otherwise clear it completely
        if (clickedTruck) {
          setActiveMarkerId(clickedTruck.id);
        } else {
          setActiveMarkerId(null);
        }
      }
    },
    [clickedTruck],
  );

  /**
   * Handle DeckGL truck click events.
   * Transitions from hover to click popover state.
   * Converts DeckGL canvas coordinates to viewport coordinates for popover positioning.
   */
  const handleTruckClick = useCallback((info: TruckPickInfo) => {
    setClickedTruck(info.truck);
    // Convert canvas-relative coordinates to viewport coordinates
    const rect = mapContainerRef.current?.getBoundingClientRect();
    const offsetX = rect?.left ?? 0;
    const offsetY = rect?.top ?? 0;
    setPopoverAnchorPosition({ x: info.x + offsetX, y: info.y + offsetY });
    setActiveMarkerId(info.truck.id);
  }, []);

  /**
   * Handle popover close - clears both hover and click state.
   */
  const handlePopoverClose = useCallback(() => {
    setHoveredTruck(null);
    setClickedTruck(null);
    setPopoverAnchorPosition(null);
    setActiveMarkerId(null);
  }, []);

  /**
   * Handle click inside the popover to "pin" it.
   * Persists the hovered truck as the clicked truck.
   */
  const handlePopoverClickTruck = useCallback((truck: TruckLayerDatum) => {
    setClickedTruck(truck);
    setActiveMarkerId(truck.id);
  }, []);

  /**
   * Create DeckGL truck layers using the custom hook.
   * Returns IconLayer for truck icons and TextLayer for labels.
   */
  const { layers: truckLayers } = useTruckLayer(regularTrucks, {
    onHover: handleTruckHover,
    onClick: handleTruckClick,
    activeMarkerId,
    showLabels: true,
  });

  /**
   * Convert job/order waypoint sites to LiveSiteData format.
   * - For order/job specific views (Dispatch expanded): only includes order/job waypoint sites
   * - For main Live Map view: merges live sites with any additional waypoint sites
   * This ensures the correct sites are rendered via DeckGL layers based on context.
   */
  const combinedSiteMarkersById = useMemo(
    () =>
      buildCombinedSiteMarkers({
        liveSiteMarkersById,
        job,
        jobSiteUsages,
        order,
        orderSiteUsages,
        loads,
        geofenceRouteJob,
      }),
    [
      liveSiteMarkersById,
      job,
      order,
      loads,
      geofenceRouteJob,
      orderSiteUsages,
      jobSiteUsages,
    ],
  );

  /**
   * Transform site data into DeckGL-compatible format for site pins.
   * Sites are rendered via GPU-accelerated IconLayer for performance.
   * Includes both live sites and job/order waypoint sites.
   */
  const siteLayerData = useMemo(
    () =>
      transformSiteLocations(combinedSiteMarkersById, {
        activeMarkerId: activeSiteId,
      }),
    [combinedSiteMarkersById, activeSiteId],
  );

  /**
   * Extract circle geofences from site data for DeckGL ScatterplotLayer.
   * Circles are sorted by radius descending so smaller geofences render on top.
   * Includes both primary and additional geofences with tag-based coloring.
   */
  const circleGeofenceData = useMemo(
    () => extractCircleGeofences(combinedSiteMarkersById, additionalGeofencesBySiteId),
    [combinedSiteMarkersById, additionalGeofencesBySiteId],
  );

  /**
   * Extract polygon geofences from site data for DeckGL GeoJsonLayer.
   * Polygons are sorted by area descending so smaller geofences render on top.
   * Includes both primary and additional geofences with tag-based coloring.
   */
  const polygonGeofenceData = useMemo(
    () => extractPolygonGeofences(combinedSiteMarkersById, additionalGeofencesBySiteId),
    [combinedSiteMarkersById, additionalGeofencesBySiteId],
  );

  /**
   * Handle DeckGL site hover events.
   * Updates active site ID for icon highlighting.
   */
  const handleSiteHover = useCallback((info: SitePickInfo | null) => {
    if (info) {
      setActiveSiteId(info.site.id);
    } else {
      setActiveSiteId(null);
    }
  }, []);

  /**
   * Handle DeckGL site click events.
   * Opens the full site details popover.
   */
  const handleSiteClick = useCallback((info: SitePickInfo) => {
    setClickedSite(info.site);
    // Clear any clicked geofence when clicking a site
    setClickedGeofence(null);
    // Convert canvas-relative coordinates to viewport coordinates
    const rect = mapContainerRef.current?.getBoundingClientRect();
    const offsetX = rect?.left ?? 0;
    const offsetY = rect?.top ?? 0;
    setSitePopoverAnchorPosition({ x: info.x + offsetX, y: info.y + offsetY });
    setActiveSiteId(info.site.id);
  }, []);

  /**
   * Handle DeckGL geofence hover events.
   * Updates hovered geofence ID for visual highlighting.
   */
  const handleGeofenceHover = useCallback((info: GeofencePickInfo | null) => {
    if (info) {
      setHoveredGeofenceId(info.geofence.id);
    } else {
      setHoveredGeofenceId(null);
    }
  }, []);

  /**
   * Handle DeckGL geofence click events.
   * Opens the lightweight geofence info popover.
   */
  const handleGeofenceClick = useCallback((info: GeofencePickInfo) => {
    setClickedGeofence(info.geofence);
    setIsClickedGeofenceCircle(info.isCircle);
    // Clear any clicked site when clicking a geofence
    setClickedSite(null);
    // Convert canvas-relative coordinates to viewport coordinates
    const rect = mapContainerRef.current?.getBoundingClientRect();
    const offsetX = rect?.left ?? 0;
    const offsetY = rect?.top ?? 0;
    setSitePopoverAnchorPosition({ x: info.x + offsetX, y: info.y + offsetY });
  }, []);

  /**
   * Handle site/geofence popover close.
   */
  const handleSitePopoverClose = useCallback(() => {
    setClickedSite(null);
    setClickedGeofence(null);
    setSitePopoverAnchorPosition(null);
    setActiveSiteId(null);
    setHoveredGeofenceId(null);
  }, []);

  /**
   * Create DeckGL site layers using the custom hook.
   * Returns IconLayer for site pin icons.
   */
  const { layers: siteLayers } = useSiteLayer(siteLayerData, {
    onHover: handleSiteHover,
    onClick: handleSiteClick,
    activeMarkerId: activeSiteId,
  });

  /**
   * Create DeckGL geofence layers using the custom hook.
   * Returns ScatterplotLayer for circles and GeoJsonLayer for polygons.
   */
  const { layers: geofenceLayers } = useGeofenceLayer(
    circleGeofenceData,
    polygonGeofenceData,
    {
      onHover: handleGeofenceHover,
      onClick: handleGeofenceClick,
      hoveredGeofenceId,
    },
  );

  /**
   * Create markers data for moving sites (these still need Google Maps circles).
   * Moving sites are rendered differently because they need variable-radius circles
   * that scale with the map, which is more naturally handled by Google Maps Circle API.
   */
  const movingSiteMarkers = useMemo(
    () =>
      movingSites.map((site) => ({
        id: site.id,
        type: MarkerType.moving_site,
        lat: site.position[1],
        lng: site.position[0],
        circleCenter: {
          lat: site.position[1],
          lng: site.position[0],
        },
        radii: site.radii ?? [],
      })),
    [movingSites],
  );

  /**
   * Create hidden markers from DeckGL site layer data for bounds calculation.
   * These markers ensure the initial map bounds include all site pin positions
   * even when sites are rendered via DeckGL layers instead of Google Maps markers.
   */
  const sitePositionMarkers = useMemo(
    (): TreadMarkerProps[] =>
      siteLayerData.map((site) => ({
        id: `site-bounds-${site.id}`,
        type: MarkerType.hidden_marker,
        lat: site.position[1],
        lng: site.position[0],
      })),
    [siteLayerData],
  );

  /**
   * Create hidden markers for geofence boundaries to ensure bounds calculation
   * includes all geofences. For circles, we add the north/south/east/west extremes.
   * For polygons, we add all corner points.
   */
  const geofenceBoundsMarkers = useMemo((): TreadMarkerProps[] => {
    const boundsMarkers: TreadMarkerProps[] = [];

    // Circle geofence bounds (N/S/E/W points of largest circle per site)
    // Group circles by siteId to only process the largest radius
    const largestCircleBySite = new Map<string, CircleGeofenceDatum>();
    circleGeofenceData.forEach((geofence) => {
      const existing = largestCircleBySite.get(geofence.siteId);
      if (!existing || geofence.radiusMeters > existing.radiusMeters) {
        largestCircleBySite.set(geofence.siteId, geofence);
      }
    });

    largestCircleBySite.forEach((geofence) => {
      const lat = geofence.center[1];
      const lng = geofence.center[0];
      // Convert radius from meters to approximate degrees
      // 111139 meters ≈ 1 degree of latitude
      const radiusLatDegrees = geofence.radiusMeters / 111139;
      const radiusLngDegrees =
        geofence.radiusMeters / (111139 * Math.cos((lat * Math.PI) / 180));

      // North point
      boundsMarkers.push({
        id: `geofence-${geofence.id}-n`,
        type: MarkerType.hidden_marker,
        lat: lat + radiusLatDegrees,
        lng: lng,
      });
      // South point
      boundsMarkers.push({
        id: `geofence-${geofence.id}-s`,
        type: MarkerType.hidden_marker,
        lat: lat - radiusLatDegrees,
        lng: lng,
      });
      // East point
      boundsMarkers.push({
        id: `geofence-${geofence.id}-e`,
        type: MarkerType.hidden_marker,
        lat: lat,
        lng: lng + radiusLngDegrees,
      });
      // West point
      boundsMarkers.push({
        id: `geofence-${geofence.id}-w`,
        type: MarkerType.hidden_marker,
        lat: lat,
        lng: lng - radiusLngDegrees,
      });
    });

    // Polygon geofence bounds (all corner points)
    polygonGeofenceData.forEach((geofence) => {
      if (geofence.coordinates && geofence.coordinates[0]) {
        geofence.coordinates[0].forEach((coord, index) => {
          boundsMarkers.push({
            id: `geofence-${geofence.id}-${index}`,
            type: MarkerType.hidden_marker,
            lat: coord[1],
            lng: coord[0],
          });
        });
      }
    });

    return boundsMarkers;
  }, [circleGeofenceData, polygonGeofenceData]);

  const getMarkerType = (waypointType: string) => {
    if (waypointType === 'pickup') {
      return MarkerType.pickup_site;
    } else if (waypointType === 'drop_off') {
      return MarkerType.dropoff_site;
    } else if (waypointType === 'both') {
      return MarkerType.both_site;
    }
    return MarkerType.site;
  };

  const createMarkerFromWayPoint = (
    wayPoint: WayPoint,
    popoverContent: ReactElement,
  ) => ({
    // Sorry for all the casting. Filter above protects us. Many nullable fields in the origin shape.
    id: wayPoint?.id as string,
    siteId: wayPoint?.site?.id as string,
    type: wayPoint?.site?.nextBillionGeofence?.movingGeofence
      ? MarkerType.moving_site
      : getMarkerType(wayPoint.type),
    lat: wayPoint?.site?.lat as number,
    lng: wayPoint?.site?.lon as number,
    // Conditionally add radius if a circle
    ...(wayPoint?.site?.nextBillionGeofence?.geofenceType === 'circle' ||
    wayPoint?.site?.nextBillionGeofence?.geofenceType === 'moving'
      ? {
          geofenceType: GeofenceType.circle,
          radii: [wayPoint?.site?.nextBillionGeofence?.circleRadius],
          circleCenter: {
            lat:
              wayPoint?.site?.nextBillionGeofence?.circleCenter?.lat ??
              wayPoint?.site?.lat,
            lng:
              wayPoint?.site?.nextBillionGeofence?.circleCenter?.lon ??
              wayPoint?.site?.lon,
          },
        }
      : {}),
    // Conditionally add coordinates if a polygon
    ...(wayPoint?.site?.nextBillionGeofence?.geofenceType === 'polygon'
      ? {
          geofenceType: GeofenceType.polygon,
          coordinates: wayPoint?.site?.nextBillionGeofence?.geojson?.coordinates,
        }
      : {}),
    clickPopoverContent: popoverContent,
  });

  /**
   * Create markers for additional geofences associated with a site.
   * These are rendered as separate circle/polygon markers with distinct styling.
   */
  const createAdditionalGeofenceMarkers = (
    siteId: string,
    siteLat: number,
    siteLon: number,
  ) => {
    const additionalGeofences = additionalGeofencesBySiteId[siteId] || [];
    return additionalGeofences
      .map((geofence, geoIndex) => {
        const baseId = `additional-geofence-${siteId}-${geoIndex}`;
        // Use geofence.type (lowercase string) not geofence.geofenceType (enum)
        if (geofence.type === 'circle') {
          return {
            id: baseId,
            siteId,
            type: MarkerType.site,
            lat: geofence.circleCenter?.lat ?? siteLat,
            lng: geofence.circleCenter?.lon ?? siteLon,
            geofenceType: GeofenceType.circle,
            radii: [geofence.circleRadius],
            circleCenter: {
              lat: geofence.circleCenter?.lat ?? siteLat,
              lng: geofence.circleCenter?.lon ?? siteLon,
            },
            isAdditionalGeofence: true,
            additionalGeofenceIndex: geoIndex,
          };
        } else if (geofence.type === 'polygon' && geofence.geojson?.coordinates) {
          return {
            id: baseId,
            siteId,
            type: MarkerType.site,
            lat: siteLat,
            lng: siteLon,
            geofenceType: GeofenceType.polygon,
            coordinates: geofence.geojson.coordinates,
            isAdditionalGeofence: true,
            additionalGeofenceIndex: geoIndex,
          };
        }
        return null;
      })
      .filter(Boolean);
  };

  const createMarkerFromSite = (site: Site, waypointType: WaypointType) => ({
    id: site.id,
    siteId: site.id,
    type: site.nextBillionGeofence?.movingGeofence
      ? MarkerType.moving_site
      : getMarkerType(waypointType),
    lat: site.lat as number,
    lng: site.lon as number,
    ...(site.nextBillionGeofence?.geofenceType === 'circle' ||
    site.nextBillionGeofence?.geofenceType === 'moving'
      ? {
          geofenceType: GeofenceType.circle,
          radii: [site.nextBillionGeofence?.circleRadius],
          circleCenter: {
            lat: site.nextBillionGeofence?.circleCenter?.lat ?? site.lat,
            lng: site.nextBillionGeofence?.circleCenter?.lon ?? site.lon,
          },
        }
      : {}),
    ...(site.nextBillionGeofence?.geofenceType === 'polygon'
      ? {
          geofenceType: GeofenceType.polygon,
          coordinates: site.nextBillionGeofence?.geojson?.coordinates,
        }
      : {}),
    clickPopoverContent: <SitePopover site={site} />,
  });

  const orderSiteMarkers = useMemo(() => {
    if (!siteDetailsBySiteId || orderSiteUsages.length === 0) return [];
    return orderSiteUsages
      .filter((su) => {
        const site = siteDetailsBySiteId[su.site.id];
        return site?.lat && site?.lon;
      })
      .map((su) => {
        const site = siteDetailsBySiteId[su.site.id];
        const waypointType = su.usages[0]?.waypointType ?? WaypointType.PICKUP;
        return createMarkerFromSite(site, waypointType);
      });
  }, [orderSiteUsages, siteDetailsBySiteId]);

  const orderMarkers = useMemo(() => {
    const baseMarkers = [...orderSiteMarkers, ...movingSiteMarkers];

    const additionalMarkers = orderSiteMarkers.flatMap((marker) =>
      createAdditionalGeofenceMarkers(marker.siteId, marker.lat, marker.lng),
    );

    return [...baseMarkers, ...additionalMarkers];
  }, [orderSiteMarkers, movingSiteMarkers, additionalGeofencesBySiteId]);

  const jobSiteMarkers = useMemo(() => {
    return job?.waypoints
      ?.filter((wayPoint) => !!wayPoint && !!wayPoint?.site?.lat && !!wayPoint?.site?.lon)
      .map((wayPoint) =>
        createMarkerFromWayPoint(
          wayPoint,
          <SitePopover
            site={siteDetailsBySiteId?.[wayPoint.site?.id ?? ''] ?? wayPoint.siteNested!}
          />,
        ),
      );
  }, [job, siteDetailsBySiteId]);

  const loadSiteMarkers = useMemo(() => {
    return loads?.map((load) => {
      return load?.waypoints
        ?.filter(
          (wayPoint) => !!wayPoint && !!wayPoint?.site?.lat && !!wayPoint?.site?.lon,
        )
        .map((wayPoint) =>
          createMarkerFromWayPoint(
            wayPoint,
            <SitePopover
              site={
                siteDetailsBySiteId?.[wayPoint.site?.id ?? ''] ?? wayPoint.siteNested!
              }
            />,
          ),
        );
    });
  }, [loads, siteDetailsBySiteId]);

  const jobMarkers = useMemo(() => {
    // Create a Map to store unique markers by siteId
    const uniqueMarkers = new Map();

    // Process job site markers first
    jobSiteMarkers?.forEach((marker) => {
      if (marker.siteId) {
        uniqueMarkers.set(marker.siteId, marker);
      }
    });

    // Process load site markers, only adding if siteId doesn't exist
    loadSiteMarkers?.flat().forEach((marker) => {
      if (marker.siteId && !uniqueMarkers.has(marker.siteId)) {
        uniqueMarkers.set(marker.siteId, marker);
      }
    });

    // Convert Map values back to array
    const baseMarkers = Array.from(uniqueMarkers.values());

    // Get all unique site IDs for additional geofence lookup
    const siteIds = Array.from(uniqueMarkers.keys());
    const additionalMarkers = siteIds.flatMap((siteId) => {
      const marker = uniqueMarkers.get(siteId);
      if (marker?.lat && marker?.lng) {
        return createAdditionalGeofenceMarkers(siteId, marker.lat, marker.lng);
      }
      return [];
    });

    if (isTimelineVisible) {
      // For route history, filter out moving sites and only show static historical sites
      const historicalMarkers = baseMarkers.filter(
        (marker) => marker.type !== MarkerType.moving_site,
      );
      return [...historicalMarkers, ...additionalMarkers];
    } else {
      // For live map, include moving site markers
      // Regular trucks are rendered via DeckGL layer, not included here
      return [...baseMarkers, ...movingSiteMarkers, ...additionalMarkers];
    }
  }, [
    jobSiteMarkers,
    loadSiteMarkers,
    movingSiteMarkers,
    job,
    isTimelineVisible,
    additionalGeofencesBySiteId,
  ]);

  // Create site markers for geofence route (when viewing route from Geofence Reports)
  const geofenceRouteSiteMarkers = useMemo(() => {
    if (!geofenceRouteJob?.waypoints || geofenceRouteJob.waypoints.length === 0) {
      return [];
    }

    return geofenceRouteJob.waypoints
      .filter((wayPoint) => !!wayPoint && !!wayPoint?.site?.lat && !!wayPoint?.site?.lon)
      .map((wayPoint) =>
        createMarkerFromWayPoint(
          wayPoint,
          <SitePopover
            site={siteDetailsBySiteId?.[wayPoint.site?.id ?? ''] ?? wayPoint.siteNested!}
          />,
        ),
      );
  }, [geofenceRouteJob, siteDetailsBySiteId]);

  const buildRoutePingDot = (ping: LocationHistoryMinimal) => {
    let fillColor;
    let borderColor;
    if (ping.idle) {
      fillColor = hexToRgbaArray('#FDE68A', 255);
      borderColor = hexToRgbaArray('#FFA000', 255 * 0.85);
    } else if (ping.offlineMode) {
      fillColor = hexToRgbaArray(theme.brandV2.colors.treadGray7, 255);
      borderColor = hexToRgbaArray(theme.brandV2.colors.treadGray5, 255 * 0.85);
    } else {
      fillColor = hexToRgbaArray('#FFBD80', 255);
      borderColor = hexToRgbaArray(theme.brandV2.colors.treadOrangeDark, 255 * 0.85);
    }

    return {
      borderColor,
      fillColor,
      lat: Number.parseFloat(ping.lat),
      lineWidth: 3,
      lng: Number.parseFloat(ping.lon),
      radius: ROUTE_MARKER_RADIUS,
    };
  };

  const routePingLayer = useMemo(
    () =>
      buildRoutePingLayer('route-layer', (jobRoutePings ?? []).map(buildRoutePingDot)),
    [jobRoutePings],
  );

  const geofenceRoutePingLayer = useMemo(
    () =>
      buildRoutePingLayer(
        'geofence-route-layer',
        (geofenceRoutePings ?? []).map(buildRoutePingDot),
      ),
    [geofenceRoutePings],
  );

  const movingSiteRouteHighlightLayer = useMemo(() => {
    if (!jobMovingSiteRoutePings || isNil(selectedJobMovingSiteRoutePingInd)) return null;

    const selectedMovingSitePing =
      jobMovingSiteRoutePings[selectedJobMovingSiteRoutePingInd];
    const movingSiteCoordinates = [
      Number.parseFloat(selectedMovingSitePing.lon),
      Number.parseFloat(selectedMovingSitePing.lat),
    ];
    const highlightPointMovingSite = point(movingSiteCoordinates, {
      radius: HIGHLIGHT_MARKER_RADIUS,
      color: hexToRgbaArray('#000000', 255),
      lineWidth: 2,
    });

    const data = [highlightPointMovingSite];

    return new GeoJsonLayer({
      id: 'moving-site-highlight-layer',
      data: data,
      getFillColor: (feature: OverlayFeature) => feature.properties.color,
      getPointRadius: (feature: OverlayFeature) => feature.properties.radius,
      stroked: true,
      getLineColor: [255, 255, 255, 255],
      getLineWidth: (feature: OverlayFeature) => feature.properties.lineWidth,
      pointRadiusUnits: 'pixels',
      lineWidthUnits: 'pixels',
    });
  }, [jobMovingSiteRoutePings, selectedJobMovingSiteRoutePingInd]);

  // this has to be in a separate layer since we want to use meters as the pointRadiusUnits
  // this ensures geofence size is accurate on the map and adapts to zoom level
  const movingSiteGeofenceHighlightLayer = useMemo(() => {
    if (!jobMovingSiteRoutePings || isNil(selectedJobMovingSiteRoutePingInd)) return null;

    if (!movingSiteData.radius) return null;

    const selectedMovingSitePing =
      jobMovingSiteRoutePings[selectedJobMovingSiteRoutePingInd];
    const movingSiteCoordinates = [
      Number.parseFloat(selectedMovingSitePing.lon),
      Number.parseFloat(selectedMovingSitePing.lat),
    ];

    const highlightPointMovingSiteHalo = [
      point(movingSiteCoordinates, {
        radius: Math.round(movingSiteData.radius),
        color: hexToRgbaArray('#000000', 255 * 0.2),
        lineWidth: 0,
      }),
    ];

    return new GeoJsonLayer({
      id: 'moving-site-geofence-highlight-layer',
      data: highlightPointMovingSiteHalo,
      getFillColor: (feature: OverlayFeature) => feature.properties.color,
      getPointRadius: (feature: OverlayFeature) => feature.properties.radius,
      stroked: true,
      getLineColor: [255, 255, 255, 255],
      getLineWidth: (feature: OverlayFeature) => feature.properties.lineWidth,
      pointRadiusUnits: 'meters',
      lineWidthUnits: 'pixels',
    });
  }, [jobMovingSiteRoutePings, selectedJobMovingSiteRoutePingInd, movingSiteData]);

  // Single highlighted point: external (table hover) takes precedence, then timeline selection.
  // Source determines highlight color: geofence route uses orange to match the route line.
  type HighlightSource = 'job-route' | 'geofence-route' | 'external';
  const displayedHighlightPoint = useMemo((): {
    lat: number;
    lon: number;
    source: HighlightSource;
  } | null => {
    if (highlightedPoint?.lat && highlightedPoint?.lon) {
      const lat = Number.parseFloat(highlightedPoint.lat);
      const lon = Number.parseFloat(highlightedPoint.lon);
      if (!Number.isNaN(lat) && !Number.isNaN(lon))
        return { lat, lon, source: 'external' };
    }
    if (jobRoutePings && !isNil(selectedJobRoutePingInd)) {
      const selectedPing = jobRoutePings[selectedJobRoutePingInd];
      if (selectedPing) {
        const lat = Number.parseFloat(selectedPing.lat);
        const lon = Number.parseFloat(selectedPing.lon);
        if (!Number.isNaN(lat) && !Number.isNaN(lon))
          return { lat, lon, source: 'job-route' };
      }
    }
    if (geofenceRoutePings && !isNil(selectedGeofenceRoutePingInd)) {
      const selectedPing = geofenceRoutePings[selectedGeofenceRoutePingInd];
      if (selectedPing) {
        const lat = Number.parseFloat(selectedPing.lat);
        const lon = Number.parseFloat(selectedPing.lon);
        if (!Number.isNaN(lat) && !Number.isNaN(lon))
          return { lat, lon, source: 'geofence-route' };
      }
    }
    return null;
  }, [
    highlightedPoint,
    jobRoutePings,
    selectedJobRoutePingInd,
    geofenceRoutePings,
    selectedGeofenceRoutePingInd,
  ]);

  // Single highlight layer for timeline selection or table hover (only one point ever shown).
  // Geofence route uses orange to match geofence route line; job route and external use blue.
  const unifiedHighlightLayer = useMemo(() => {
    if (!displayedHighlightPoint) return null;

    const isGeofenceRoute = displayedHighlightPoint.source === 'geofence-route';
    const highlightColor = isGeofenceRoute
      ? theme.brandV2.colors.treadOrangeDark
      : '#0044FF';

    const coordinates = [displayedHighlightPoint.lon, displayedHighlightPoint.lat];
    const highlightPoint = point(coordinates, {
      radius: HIGHLIGHT_MARKER_RADIUS,
      color: hexToRgbaArray(highlightColor, 255),
      lineWidth: 2,
    });
    const highlightPointHalo = point(coordinates, {
      radius: HIGHLIGHT_MARKER_RADIUS * 2,
      color: hexToRgbaArray(highlightColor, 255 * 0.25),
      lineWidth: 0,
    });

    const data = [highlightPointHalo, highlightPoint];

    return new GeoJsonLayer({
      id: 'unified-highlight-layer',
      data: data,
      getFillColor: (feature: OverlayFeature) => feature.properties.color,
      getPointRadius: (feature: OverlayFeature) => feature.properties.radius,
      stroked: true,
      getLineColor: [255, 255, 255, 255],
      getLineWidth: (feature: OverlayFeature) => feature.properties.lineWidth,
      pointRadiusUnits: 'pixels',
      lineWidthUnits: 'pixels',
    });
  }, [displayedHighlightPoint, theme.brandV2.colors.treadOrangeDark]);

  const overlayLayers = useMemo(() => {
    const layers: LayersList = [];

    // Layer order (bottom to top) per specification:
    // 1. Polygon geofences (sorted by area descending - largest first)
    // 2. Circle geofences (sorted by radius descending - largest first)
    // 3. Route ping layer (if timeline visible)
    // 4. Route/moving site highlight layers
    // 5. Site pin icons
    // 6. Truck icons
    // 7. Truck labels

    // Add geofence layers FIRST (renders at bottom)
    // Geofences should always be visible, including during route history view
    if (geofenceLayers.length > 0) {
      layers.push(...geofenceLayers);
    }

    // Add route ping layer if available
    if (routePingLayer) {
      layers.push(routePingLayer);
    }

    // Add geofence route ping layer if available (from Geofence Reports)
    if (geofenceRoutePingLayer) {
      layers.push(geofenceRoutePingLayer);
    }

    // Add moving site geofence highlight
    if (movingSiteGeofenceHighlightLayer) {
      layers.push(movingSiteGeofenceHighlightLayer);
    }

    // Add moving site route highlight
    if (movingSiteRouteHighlightLayer) {
      layers.push(movingSiteRouteHighlightLayer);
    }

    // Add unified highlight layer (timeline selection or table hover - single point only)
    if (unifiedHighlightLayer) {
      layers.push(unifiedHighlightLayer);
    }

    // Add site layers (renders above geofences but below trucks)
    // Site pins should always be visible, including during route history view
    if (siteLayers.length > 0) {
      layers.push(...siteLayers);
    }

    // Add truck layers LAST (renders on top)
    // Only show truck layers when not viewing timeline (route history shows historical positions)
    if (!isTimelineVisible && truckLayers.length > 0) {
      layers.push(...truckLayers);
    }

    return layers;
  }, [
    geofenceLayers,
    siteLayers,
    truckLayers,
    routePingLayer,
    geofenceRoutePingLayer,
    unifiedHighlightLayer,
    movingSiteRouteHighlightLayer,
    movingSiteGeofenceHighlightLayer,
    isTimelineVisible,
  ]);

  const onMapConfigurationOptionsChange = (options: Record<string, boolean>) => {
    setShowVendorName(options[MapConfigurationOptionKey.VENDOR_NAME]);
    setColorByDestination(options[MapConfigurationOptionKey.DESTINATION_COLOR_CODING]);
  };

  // once the back to live button is clicked, we can override user bounds
  // this allows the map to pan back to the live pins from the order/job view
  const [isBackClicked, setIsBackClicked] = useState(false);

  // Orange pickup/dropoff icons for naive/accuracy_strict geofences (Job Geo Debug)
  const geofenceLabelMarkers = useMemo((): TreadMarkerProps[] => {
    const out: TreadMarkerProps[] = [];
    if (
      !isNil(geofencePickupCenter) &&
      !isNil(geofencePickupCenter.lat) &&
      !isNil(geofencePickupCenter.lng)
    ) {
      out.push({
        id: 'geofence-pickup-label',
        lat: geofencePickupCenter.lat,
        lng: geofencePickupCenter.lng,
        type: MarkerType.pickup_site_geo_debug,
        ...(onGeofenceLabelClick && { onClick: () => onGeofenceLabelClick('pickup') }),
        ...(onGeofenceLabelDrag && {
          onDrag: (pos: { lat: number; lng: number }) =>
            onGeofenceLabelDrag('pickup', pos),
        }),
        ...(onGeofenceLabelDragEnd && {
          onDragEnd: (pos: { lat: number; lng: number }) =>
            onGeofenceLabelDragEnd('pickup', pos),
        }),
      });
    }
    if (
      !isNil(geofenceDropoffCenter) &&
      !isNil(geofenceDropoffCenter.lat) &&
      !isNil(geofenceDropoffCenter.lng)
    ) {
      out.push({
        id: 'geofence-dropoff-label',
        lat: geofenceDropoffCenter.lat,
        lng: geofenceDropoffCenter.lng,
        type: MarkerType.dropoff_site_geo_debug,
        ...(onGeofenceLabelClick && { onClick: () => onGeofenceLabelClick('dropoff') }),
        ...(onGeofenceLabelDrag && {
          onDrag: (pos: { lat: number; lng: number }) =>
            onGeofenceLabelDrag('dropoff', pos),
        }),
        ...(onGeofenceLabelDragEnd && {
          onDragEnd: (pos: { lat: number; lng: number }) =>
            onGeofenceLabelDragEnd('dropoff', pos),
        }),
      });
    }
    return out;
  }, [
    geofencePickupCenter,
    geofenceDropoffCenter,
    onGeofenceLabelClick,
    onGeofenceLabelDrag,
    onGeofenceLabelDragEnd,
  ]);

  const noop = useCallback(() => {}, []);

  // Create a marker for the geofence configuration location pin
  const geofenceLocationMarker = useMemo(() => {
    if (!geofenceRecenterPosition) return null;
    return {
      id: 'geofence-location-marker',
      lat: geofenceRecenterPosition.lat,
      lng: geofenceRecenterPosition.lng,
      type: MarkerType.site,
      geofenceType: GeofenceType.circle,
      circleCenter: {
        lat: geofenceRecenterPosition.lat,
        lng: geofenceRecenterPosition.lng,
      },
      radii: [geofenceCircleRadius ?? 0],
      ...(onGeofenceLocationDragEnd && {
        draggable: true as const,
        onDragEnd: onGeofenceLocationDragEnd,
        onMouseDown: noop,
        onMouseUp: noop,
        onMouseOver: noop,
        onMouseOut: noop,
      }),
    } as TreadMarkerProps;
  }, [geofenceRecenterPosition, geofenceCircleRadius, onGeofenceLocationDragEnd, noop]);

  // Compute markers for Google Maps rendering (moving sites only)
  // Regular trucks and static sites are rendered via DeckGL layers for better performance
  // Site pins and geofences are rendered via DeckGL for GPU acceleration
  // For the main Live Map view (no order/job), include hidden markers for bounds calculation
  const markers = useMemo(() => {
    const geofenceMarkers = geofenceLocationMarker ? [geofenceLocationMarker] : [];

    // When viewing a geofence route (from Geofence Reports tab), show the job's site markers
    if (
      geofenceRoutePings &&
      geofenceRoutePings.length > 0 &&
      geofenceRouteSiteMarkers.length > 0
    ) {
      return [...geofenceRouteSiteMarkers, ...geofenceMarkers];
    }

    // `sitePositionMarkers` are hidden bounds-only markers derived from the
    // combined site map (which includes site_usages). They're added to the
    // order/job branches so fitBounds covers sites that have no materialized
    // waypoints yet — e.g. default-only sites on a planned job/order. Without
    // them, such a view has no markers to fit and the map stays stuck at the
    // company default center. They're hidden, so they add no visible markers.
    if (order) {
      return [...orderMarkers, ...sitePositionMarkers, ...geofenceMarkers];
    }
    if (job) {
      return [
        ...jobMarkers,
        ...sitePositionMarkers,
        ...geofenceMarkers,
        ...geofenceLabelMarkers,
      ];
    }
    // Main Live Map view: include hidden markers for bounds calculation
    // These ensure the initial map bounds include all site pins and geofences
    // even though they're rendered via DeckGL layers
    return [
      ...movingSiteMarkers,
      ...sitePositionMarkers,
      ...geofenceBoundsMarkers,
      ...geofenceMarkers,
    ];
  }, [
    order,
    job,
    orderMarkers,
    jobMarkers,
    geofenceLabelMarkers,
    movingSiteMarkers,
    sitePositionMarkers,
    geofenceBoundsMarkers,
    geofenceLocationMarker,
    geofenceRoutePings,
    geofenceRouteSiteMarkers,
  ]) as ComponentProps<typeof MapV2>['markers'];

  // if markers is empty, we get a get the furthest jobRoutePings in lat/lon directions
  const furthestJobRoutePings = useMemo(() => {
    // if there are job markers, we don't need to process the furthest job route pings
    if (jobMarkers.length > 0) return [];

    if (!jobRoutePings || jobRoutePings.length === 0) return [];

    // Sort copies to prevent direct mutations on jobRoutePings - their original sequence matters elsewhere
    const sortedByLat = [...jobRoutePings].sort((a, b) => Number(a.lat) - Number(b.lat));
    const sortedByLon = [...jobRoutePings].sort((a, b) => Number(a.lon) - Number(b.lon));
    return [
      sortedByLat[0],
      sortedByLat[sortedByLat.length - 1],
      sortedByLon[0],
      sortedByLon[sortedByLon.length - 1],
    ];
  }, [jobRoutePings]);

  const furthestJobRouteMarkers = useMemo(() => {
    return furthestJobRoutePings.map(
      (ping) =>
        ({
          id: ping.id,
          type: MarkerType.hidden_marker,
          lat: Number.parseFloat(ping.lat),
          lng: Number.parseFloat(ping.lon),
        }) as TreadMarkerProps,
    );
  }, [furthestJobRoutePings]);

  // When centerOnPoint is set (e.g. row click in Job Geo Debug), pan the map to it
  const centerOnPointLngLat = useMemo(() => {
    if (!centerOnPoint?.lat || !centerOnPoint?.lon) return null;
    const lat = Number.parseFloat(centerOnPoint.lat);
    const lng = Number.parseFloat(centerOnPoint.lon);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
    return { lat, lng };
  }, [centerOnPoint]);

  // When timeline is scrubbed, pan map to the selected ping (job route or geofence route)
  const timelineRecenterPosition = useMemo((): { lat: number; lng: number } | null => {
    if (!displayedHighlightPoint) return null;
    if (
      displayedHighlightPoint.source !== 'job-route' &&
      displayedHighlightPoint.source !== 'geofence-route'
    ) {
      return null;
    }
    return {
      lat: displayedHighlightPoint.lat,
      lng: displayedHighlightPoint.lon,
    };
  }, [displayedHighlightPoint]);

  // Pan on table row click, geofence config, or timeline scrub (not on hover)
  const effectiveRecenterPosition =
    centerOnPointLngLat ?? geofenceRecenterPosition ?? timelineRecenterPosition ?? null;
  const forceRecenter =
    !!centerOnPointLngLat || !!geofenceRecenterPosition || !!timelineRecenterPosition;

  const canToggleTimeline = !isTimelineVisible && !!onTimelineVisibilityChange;
  const showRouteHistoryAction = canToggleTimeline && !hideRouteHistoryAction;
  const showLiveMapAction = canToggleTimeline;
  const showActionRow = !!job?.id && (canToggleTimeline || !!extraActions);

  return (
    <Box
      sx={{
        flex: 1, // To handle the case where the map is the only child of a flex container
        height: '100%',
        display: 'grid',
        gridTemplateRows: '1fr',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          '&.truck-hover .gm-style *': {
            cursor: 'pointer !important',
          },
        }}
        className={hoveredTruck ? 'truck-hover' : undefined}
        ref={mapContainerRef}
      >
        {(job || order) && onBackClick && (
          <BackToLiveBar
            bottom
            handleBackOnClick={() => {
              setJobRoutePings([]);
              setIsBackClicked(true);
              onBackClick();
            }}
            selectedJob={job}
            selectedOrder={order}
          />
        )}
        <MapV2
          center={mapCenter}
          markers={markers && markers.length > 0 ? markers : furthestJobRouteMarkers}
          maxZoom={14}
          overlayLayers={overlayLayers}
          allowOverrideUserBounds={!!(job || order || isBackClicked || shouldResetBounds)}
          recenterPosition={effectiveRecenterPosition}
          forceRecenter={forceRecenter}
          onBoundsReset={handleBoundsReset}
          drawingMode={geofenceDrawingMode}
          drawingCoordinates={geofenceDrawingCoordinates}
          onPolygonEdit={onGeofencePolygonEdit}
          editableCircleCenter={
            geofenceRecenterPosition && onGeofenceCircleEdit
              ? geofenceRecenterPosition
              : null
          }
          editableCircleRadius={
            geofenceRecenterPosition && onGeofenceCircleEdit
              ? (geofenceCircleRadius ?? null)
              : null
          }
          onEditableCircleChange={onGeofenceCircleEdit}
          onClick={isMapPickMode ? onMapLocationPick : undefined}
          skipDrawingModeFitBounds={
            !!geofenceRecenterPosition ||
            !!centerOnPointLngLat ||
            !!timelineRecenterPosition
          }
          hideLayersToggle={hideMapControls}
          readOnlyGeofencePolygon={readOnlyGeofencePolygon}
          readOnlyGeofenceCircle={readOnlyGeofenceCircle}
        >
          {!hideMapControls && isGoogleMapsLoaded ? (
            <MapConfigurationControl
              position={window.google.maps.ControlPosition.TOP_LEFT}
              onOptionsChange={onMapConfigurationOptionsChange}
            />
          ) : undefined}
        </MapV2>

        {/* Single shared popover for truck interactions - replaces per-marker popovers */}
        <TruckPopoverManager
          hoveredTruck={hoveredTruck}
          clickedTruck={clickedTruck}
          anchorPosition={popoverAnchorPosition}
          onClose={handlePopoverClose}
          onHoverChange={setHoveredTruck}
          onClickTruck={handlePopoverClickTruck}
          onViewRoute={onViewJobRoute ? handleViewRoute : undefined}
          mapContainerRef={mapContainerRef}
        />

        {/* Single shared popover for site and geofence interactions */}
        <SitePopoverManager
          clickedSite={clickedSite}
          clickedGeofence={clickedGeofence}
          isClickedGeofenceCircle={isClickedGeofenceCircle}
          anchorPosition={sitePopoverAnchorPosition}
          onClose={handleSitePopoverClose}
          mapContainerRef={mapContainerRef}
        />
      </Box>
      {isTimelineVisible && (
        <>
          <Box
            sx={{
              m: 2,
              alignItems: 'center',
              gap: 2,

              display:
                isLoadingRoutePings || isLoadingMovingSiteRoutePings ? 'flex' : 'none',
            }}
          >
            <Typography
              noWrap
              variant="body1"
              sx={{
                textOverflow: 'visible',
              }}
            >
              {t('live_map.actions.loading_route_history')}
            </Typography>
            <LoadingSpinner
              isVisible={isLoadingRoutePings || isLoadingMovingSiteRoutePings}
              sx={{ width: 'fit-content' }}
            />
          </Box>
          {!!jobRoutePings && jobRoutePings.length > 0 && (
            <TimeIndicatorContextProvider>
              <Timeline
                pings={jobRoutePings}
                movingSitePings={jobMovingSiteRoutePings ?? []}
                loadCycles={loadCycles}
                showHoveredLoadStats={showHoveredLoadStats}
                pauseEvents={jobPauseResumeEvents}
                onHover={(arrInd: number, movingSiteArrInd?: number) => {
                  setSelectedJobRoutePingInd(arrInd);
                  if (!isNil(movingSiteArrInd)) {
                    setSelectedJobMovingSiteRoutePingInd(movingSiteArrInd);
                  }
                }}
                onTimelineVisibilityChange={onTimelineVisibilityChange ?? undefined}
              />
            </TimeIndicatorContextProvider>
          )}
        </>
      )}
      {/* Timeline for Geofence Reports route pings */}
      {(isLoadingGeofenceRoutePings ||
        (geofenceRoutePings && geofenceRoutePings.length > 0)) && (
        <>
          <Box
            sx={{
              m: 2,
              alignItems: 'center',
              gap: 2,
              display: isLoadingGeofenceRoutePings ? 'flex' : 'none',
            }}
          >
            <Typography
              noWrap
              variant="body1"
              sx={{
                textOverflow: 'visible',
              }}
            >
              {t('live_map.actions.loading_route_history')}
            </Typography>
            <LoadingSpinner
              isVisible={isLoadingGeofenceRoutePings}
              sx={{ width: 'fit-content' }}
            />
          </Box>
          {!!geofenceRoutePings && geofenceRoutePings.length > 0 && (
            <TimeIndicatorContextProvider>
              <Timeline
                pings={geofenceRoutePings}
                movingSitePings={[]}
                onHover={(arrInd: number) => {
                  setSelectedGeofenceRoutePingInd(arrInd);
                }}
              />
            </TimeIndicatorContextProvider>
          )}
        </>
      )}
      {showActionRow && (
        <Box sx={{ p: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
          {extraActions}
          {showRouteHistoryAction && (
            <SmallLoadingButton
              size="small"
              color="brandV2Yellow"
              loading={isLoadingRoutePings}
              onClick={() => {
                onTimelineVisibilityChange(true);
                setIsLoadingRoutePings(true);
                fetchTreadRoute(job.id).then((data) => {
                  setJobRoutePings(data);
                  setIsLoadingRoutePings(false);
                });
                if (movingSiteData.id) {
                  setIsLoadingMovingSiteRoutePings(true);
                  fetchTreadMovingGeofenceRoute(job.id, movingSiteData.id).then(
                    (data) => {
                      setJobMovingSiteRoutePings(data);
                      setIsLoadingMovingSiteRoutePings(false);
                    },
                  );
                }
              }}
              sx={{ justifySelf: 'start', fontSize: '12px', fontWeight: 'bold' }}
            >
              {t('live_map.actions.view_route_history')}
            </SmallLoadingButton>
          )}
          {showLiveMapAction && (
            <SmallButton
              color="brandV2Yellow"
              onClick={() => {
                window.open(
                  `${window.location.origin}/${ROUTES.live}/${job.id}`,
                  '_blank',
                  'noopener,noreferrer',
                );
              }}
              size="small"
              variant="contained"
              sx={{ fontSize: '12px', fontWeight: 'bold' }}
            >
              {t('live_map.actions.view_in_live_map')}
            </SmallButton>
          )}
        </Box>
      )}
    </Box>
  );
};

export default observer(TreadLiveMapV2);
